import React from 'react';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { ROOT_FOLDER_ID, folderTreeOptions, localStorageAssetLibraryPreferences } from '@ael/shared-components-library';
import { createCompanyScopedAssetLibraryClient } from '@ael/core-api';
import { Media } from '../../../pages/media/Media';
import { MEDIA_STORAGE_SCOPE } from '../../../pages/media/assetListingRouterStore';
import { getMeQueryOptions } from '../../../rest-api/queries/sharedQueries';
import { mediaSearchSchema } from '../../../routing/schemas';

export const Route = createFileRoute('/_private/_home/media/{-$folderId}')({
  validateSearch: mediaSearchSchema,
  // A bare /media reached from outside the library reopens the folder it was last left in. One the
  // library navigated to itself -- the root, chosen there, or come back to with Back -- stays put.
  beforeLoad: async ({ params, context, location }) => {
    if (params.folderId !== undefined || location.state.fromAssetLibrary) return;

    const me = await context.queryClient.ensureQueryData(getMeQueryOptions(context.apiClient));
    const lastFolderId = localStorageAssetLibraryPreferences.getLastFolder(
      me.organization.tenantGuid,
      MEDIA_STORAGE_SCOPE
    );
    if (lastFolderId === null || lastFolderId === ROOT_FOLDER_ID) return;

    // Fetched with `staleTime: 0`, not merely ensured: a folder deleted elsewhere (another tab) is
    // still in a cached tree, and the cache would hand it over. It is the same cache entry the library
    // reads, and the library mounts within the options' own stale time, so it takes this tree rather
    // than fetching its own. A tree that cannot be fetched is the library's to report from its own
    // frame, not the router's.
    const client = createCompanyScopedAssetLibraryClient(context.assetLibraryApiClient, me.organization.tenantGuid);
    const folders = await context.queryClient
      .fetchQuery({ ...folderTreeOptions(client), staleTime: 0 })
      .catch(() => undefined);
    if (folders === undefined) return;

    // Deleted since it was remembered: open the root. (`fetchQuery` hands back the flat list the
    // query function returned; `select` only runs for observers.)
    if (!folders.some((folder) => folder.id === lastFolderId)) return;

    throw redirect({
      to: '/media/{-$folderId}',
      params: { folderId: lastFolderId },
      search: location.search,
      replace: true,
    });
  },
  component: MediaRoute,
});

function MediaRoute() {
  const { apiClient, assetLibraryApiClient, queryClient } = Route.useRouteContext();
  const router = useRouter();
  return (
    <Media
      portalApiClient={apiClient}
      assetLibraryApiClient={assetLibraryApiClient}
      queryClient={queryClient}
      router={router}
    />
  );
}
